import React, { useState, useMemo } from 'react';
import { LINKEDIN_POSTS } from '../data/linkedinPosts';

/**
 * LinkedInCopyLibrary, copy-paste-ready library of all 36 LinkedIn posts.
 *
 * Designed for SCROLLING (not printing). Embedded below the strategic
 * A4 pages of the marketing playbook. Hidden from print via .no-print.
 *
 * Per-post features:
 *  - Full ready-to-publish copy in LinkedIn-style preview
 *  - One-click "Copy to clipboard" button
 *  - Pillar badge + week/day + format meta
 *  - Hashtag suggestion
 *
 * Filter bar: All / Month / Pillar
 */

const COLORS = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  ink: '#0A2540',
  slate: '#2A3B5A',
  muted: '#5B6B82',
  border: '#E5DFD3',
  bgSoft: '#F7F4EE',
  cream: '#FFF8E7',
  magenta: '#D6336C',
  magentaDeep: '#A22456',
  gold: '#C99A2E',
  emerald: '#1F7A4C',
  paper: '#FAFAF7',
};

const PILLAR_META = {
  P1: { label: 'Excel Truth', color: COLORS.magenta },
  P2: { label: 'From the Floor', color: COLORS.emerald },
  P3: { label: 'Compliance Decoded', color: COLORS.navy },
  P4: { label: 'Tactical Resource', color: COLORS.gold },
  P5: { label: "Founder's POV", color: COLORS.magentaDeep },
  P6: { label: 'Product Demo', color: '#7B5CFF' },
};

const monthOf = (week) => (week <= 4 ? 1 : week <= 8 ? 2 : 3);

export default function LinkedInCopyLibrary() {
  const [filterMonth, setFilterMonth] = useState('all'); // 'all' | 1 | 2 | 3
  const [filterPillar, setFilterPillar] = useState('all'); // 'all' | 'P1'...'P6'
  const [copiedId, setCopiedId] = useState(null);

  const filtered = useMemo(() => {
    return LINKEDIN_POSTS.filter((p) => {
      if (filterMonth !== 'all' && monthOf(p.week) !== filterMonth) return false;
      if (filterPillar !== 'all' && p.pillar !== filterPillar) return false;
      return true;
    });
  }, [filterMonth, filterPillar]);

  async function handleCopy(post) {
    const text = `${post.body}\n\n${post.hashtags}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(post.id);
      setTimeout(() => setCopiedId(null), 2200);
    } catch {
      // Fallback: select + execCommand for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopiedId(post.id);
        setTimeout(() => setCopiedId(null), 2200);
      } catch {
        /* noop */
      }
      document.body.removeChild(ta);
    }
  }

  return (
    <section
      className="no-print"
      data-testid="linkedin-copy-library"
      style={{
        background: COLORS.navyDeep,
        padding: '64px 24px 96px',
        marginTop: 48,
      }}
    >
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              color: COLORS.gold,
              fontWeight: 700,
              marginBottom: 12,
            }}
          >
            36 ready-to-publish LinkedIn posts
          </div>
          <h2
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 36,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
              margin: '0 0 14px',
            }}
          >
            The copy library.{' '}
            <em style={{ color: COLORS.magenta, fontStyle: 'italic', fontWeight: 500 }}>
              One click to clipboard.
            </em>
          </h2>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.55,
              color: 'rgba(255,255,255,0.7)',
              maxWidth: 680,
              margin: 0,
            }}
          >
            Every post is full copy, ready to paste straight into LinkedIn, written for the voice
            of Irish & UK artisan food producers (dry, specific, story-driven, no Silicon
            Valley vocabulary). Use them verbatim, or tweak them per producer you're talking to.
            Hashtags suggested at the foot of each. Print panel above stays clean for PDF.
          </p>
        </div>

        {/* Filter bar */}
        <div
          style={{
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap',
            marginBottom: 32,
            paddingBottom: 24,
            borderBottom: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <FilterGroup
            label="Month"
            options={[
              ['all', 'All 36'],
              [1, 'Month 1'],
              [2, 'Month 2'],
              [3, 'Month 3'],
            ]}
            value={filterMonth}
            onChange={setFilterMonth}
          />
          <FilterGroup
            label="Pillar"
            options={[
              ['all', 'All'],
              ...Object.entries(PILLAR_META).map(([k, v]) => [k, v.label]),
            ]}
            value={filterPillar}
            onChange={setFilterPillar}
          />
        </div>

        {/* Post cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filtered.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onCopy={() => handleCopy(post)}
              copied={copiedId === post.id}
            />
          ))}
          {filtered.length === 0 && (
            <div
              style={{
                padding: '48px 24px',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.5)',
                fontSize: 14,
              }}
            >
              No posts match those filters.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function FilterGroup({ label, options, value, onChange }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.45)',
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map(([v, label]) => {
          const active = String(value) === String(v);
          return (
            <button
              key={v}
              data-testid={`filter-${label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => onChange(v)}
              style={{
                padding: '6px 12px',
                background: active ? COLORS.magenta : 'rgba(255,255,255,0.06)',
                color: active ? '#fff' : 'rgba(255,255,255,0.75)',
                border: active
                  ? `1px solid ${COLORS.magenta}`
                  : '1px solid rgba(255,255,255,0.12)',
                borderRadius: 16,
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: '0.01em',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PostCard({ post, onCopy, copied }) {
  const pillar = PILLAR_META[post.pillar];
  return (
    <article
      data-testid={`post-card-${post.id}`}
      style={{
        background: '#fff',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      }}
    >
      {/* Header strip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px',
          background: COLORS.bgSoft,
          borderBottom: `1px solid ${COLORS.border}`,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              padding: '3px 10px',
              background: pillar.color,
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: '0.06em',
              borderRadius: 4,
              textTransform: 'uppercase',
            }}
          >
            {post.pillar} · {pillar.label}
          </span>
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: COLORS.navy,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Week {post.week}
          </span>
          <span style={{ fontSize: 11.5, color: COLORS.muted }}>{post.day}</span>
          <span style={{ fontSize: 11.5, color: COLORS.muted }}>· {post.format}</span>
        </div>
        <button
          onClick={onCopy}
          data-testid={`copy-btn-${post.id}`}
          style={{
            padding: '6px 14px',
            background: copied ? COLORS.emerald : COLORS.navy,
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.04em',
            cursor: 'pointer',
            textTransform: 'uppercase',
            transition: 'background 0.15s ease',
          }}
        >
          {copied ? '✓ Copied' : 'Copy to clipboard'}
        </button>
      </div>

      {/* Title */}
      <div style={{ padding: '14px 18px 6px' }}>
        <div
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 18,
            fontWeight: 700,
            color: COLORS.navy,
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
          }}
        >
          {post.title}
        </div>
      </div>

      {/* LinkedIn-style post body */}
      <div
        style={{
          padding: '6px 18px 14px',
          fontFamily:
            "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 14.5,
          lineHeight: 1.55,
          color: COLORS.ink,
          whiteSpace: 'pre-wrap',
        }}
      >
        {post.body}
      </div>

      {/* Hashtag bar */}
      <div
        style={{
          padding: '10px 18px',
          background: '#fcfaf6',
          borderTop: `1px solid ${COLORS.border}`,
          fontSize: 12.5,
          color: COLORS.magenta,
          fontWeight: 600,
          letterSpacing: '0.01em',
        }}
      >
        {post.hashtags}
      </div>
    </article>
  );
}
